import { ApexOptions } from "apexcharts"
import { useMemo } from "react"
import ReactApexChart from "react-apexcharts"
import { monthDayLabels } from "../../../chartDateAxis"
import { dailyTotals, windowTotals } from "../../../performanceSnapshot"

/*
    Spend Per User and Impressions Per User, over time (MPN-1548).

    The Spend tab reports both as a single number for the window. That says how the
    campaign has done overall and nothing about whether it is getting better or worse,
    which is the question a rate invites. These are the same two ratios plotted daily.

    One component, two instances. The charts differ only in what they divide and how the
    result is formatted; giving them separate files would be two copies of the same axis,
    tooltip and denominator handling, and the next change would have to be made twice.

    On the denominator: Selected is a current-state count, not a windowed one -- the same
    figure the tiles divide by, and the same caveat. So a point means "this day's spend
    against the list we have selected today", not "against the list as it stood that day".
    We do not keep a daily history of selection counts, and inventing one by back-dating
    today's would be worse than the honest version: it would draw a trend out of a number
    that never moved.

    Cumulative was considered and rejected. A cumulative per-user line only ever rises,
    so it cannot show a campaign getting more or less efficient, which is the whole point
    of asking for it over time.
*/

type Mode = "spend" | "impressions"

const CONFIG: Record<Mode, {
  color: string
  axisTitle: string
  format: (v: number) => string
  empty: string
}> = {
  spend: {
    color: "#6366f1",
    axisTitle: "Spend per user",
    // Four decimals, because a day's spend across thousands of matched users is
    // routinely fractions of a cent and $0.00 on every point is not a chart.
    format: (v) => `$${v.toFixed(4)}`,
    empty: "No spend in this date range.",
  },
  impressions: {
    color: "#0ea5e9",
    axisTitle: "Impressions per user",
    format: (v) => v.toFixed(2),
    empty: "No impressions in this date range.",
  },
}

export default function PerUserTrend({ data, mode }: { data: any; mode: Mode }) {
  const config = CONFIG[mode]

  const { series, days, selected } = useMemo(() => {
    const { selected } = windowTotals(data)
    const rows = dailyTotals(data?.["performance-by-day"])

    const days = rows.map((r) => r.day)
    const series = [
      {
        name: config.axisTitle,
        data: rows.map((r) => {
          const numerator = mode === "spend" ? r.spend : r.impressions
          return selected > 0 ? numerator / selected : 0
        }),
      },
    ]

    return { series, days, selected }
  }, [data, mode, config.axisTitle])

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "line",
        height: 260,
        toolbar: { show: false },
        animations: { enabled: false },
        zoom: { enabled: false },
      },
      colors: [config.color],
      dataLabels: { enabled: false },
      // Straight, for the same reason as the conversions lines: a spline invents
      // values between two days that were never measured.
      stroke: { width: 2, curve: "straight" },
      markers: { size: 3, strokeWidth: 0, hover: { size: 5 } },
      grid: { borderColor: "#f1f1f1" },
      xaxis: { categories: days, tickAmount: 8, labels: monthDayLabels },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: { formatter: (v: number) => config.format(v ?? 0) },
        title: { text: config.axisTitle },
      },
      legend: { show: false },
      tooltip: {
        intersect: false,
        shared: false,
        y: { formatter: (v: number) => config.format(v ?? 0) },
      },
    }),
    [days, config],
  )

  /*
      Without a selected count there is no per-user anything. Saying so beats a flat
      line on zero, which reads as "we spent nothing on them" rather than "we do not
      know how many of them there are".
  */
  if (selected <= 0) {
    return (
      <p className="text-muted fs-11 mb-0">
        No selected audience yet, so per-user figures cannot be calculated. This fills in
        once an audience has been selected.
      </p>
    )
  }

  if (days.length === 0) {
    return <p className="text-muted fs-11 mb-0">{config.empty}</p>
  }

  return <ReactApexChart options={options} series={series} type="line" height={260} />
}
